import { Fragment, useState } from "react";
import { formatCount, formatMoney, MINUS } from "@shared/roi/format";
import type { ScenarioModel, YearResult } from "@shared/roi/types";
import { packageColorVar } from "@client/lib/packageColors";

/**
 * Every figure the charts draw, as text.
 *
 * This is not a fallback — it is the accessible equivalent of the three charts
 * above it and is always present, never behind a toggle (docs/08-frontend.md).
 * A year expands into a row per package plus the line-item total, which is the
 * one place the whole projection can be read without a mouse.
 */

interface SubRow {
  key: string;
  label: string;
  color?: string;
  newClients?: number;
  churn?: number;
  active?: number;
  revenue: number;
  cost: number;
}

export function ProjectionTable({
  model,
  rows,
  currencyCode,
}: {
  model: ScenarioModel;
  rows: YearResult[];
  currencyCode: string;
}) {
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set());
  const packages = [...model.packages].sort((a, b) => a.sortOrder - b.sortOrder);

  const toggle = (year: number) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(year)) next.add(year);
      return next;
    });

  const money = (value: number) => formatMoney(currencyCode, value);
  const tone = (value: number) => (value >= 0 ? "var(--roi-revenue)" : "var(--roi-cost)");

  const subRowsFor = (row: YearResult): SubRow[] => {
    const subRows: SubRow[] = packages.map((pkg) => {
      const breakdown = row.byPackage[pkg.id];
      return {
        key: pkg.id,
        label: pkg.name.trim() || "Untitled package",
        color: packageColorVar(pkg.colorIndex),
        newClients: row.newByPackage[pkg.id] ?? 0,
        churn: row.churnByPackage[pkg.id] ?? 0,
        active: row.activeByPackage[pkg.id] ?? 0,
        revenue: breakdown?.revenue ?? 0,
        cost: breakdown?.cost ?? 0,
      };
    });

    if (row.unassigned > 0) {
      subRows.push({
        key: "unassigned",
        label: "Unassigned — no package",
        newClients: row.unassigned,
        revenue: 0,
        cost: 0,
      });
    }
    if (row.lineItemRevenue !== 0 || row.lineItemCost !== 0) {
      subRows.push({
        key: "line-items",
        label: "Costs & revenues",
        revenue: row.lineItemRevenue,
        cost: row.lineItemCost,
      });
    }
    return subRows;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-base">
        <caption className="sr-only">
          Clients, revenue, cost and net position for each year of the projection.
        </caption>
        <thead>
          {/* Sticky against the page scroll. The KPI strip sits above it on a
              higher layer, which is the right order: on a long horizon the
              totals matter more than the column names. */}
          <tr className="sticky top-0 z-10 bg-[var(--haze)] text-left text-xs uppercase tracking-wider text-ink-soft">
            <th scope="col" className="border-b border-line py-2 pr-3 font-bold">
              Year
            </th>
            <th scope="col" className="border-b border-line py-2 pr-3 text-right font-bold">
              New
            </th>
            <th scope="col" className="border-b border-line py-2 pr-3 text-right font-bold">
              Churn
            </th>
            <th scope="col" className="border-b border-line py-2 pr-3 text-right font-bold">
              Active
            </th>
            <th scope="col" className="border-b border-line py-2 pr-3 text-right font-bold">
              Revenue
            </th>
            <th scope="col" className="border-b border-line py-2 pr-3 text-right font-bold">
              Cost
            </th>
            <th scope="col" className="border-b border-line py-2 pr-3 text-right font-bold">
              Net
            </th>
            <th scope="col" className="border-b border-line py-2 text-right font-bold">
              Cumulative
            </th>
          </tr>
        </thead>
        <tbody className="tabular">
          {rows.map((row) => {
            const expanded = open.has(row.year);
            return (
              <Fragment key={row.year}>
                <tr className="border-line/60 border-b">
                  <th scope="row" className="py-2 pr-3 text-left font-semibold text-ink">
                    <button
                      type="button"
                      onClick={() => toggle(row.year)}
                      aria-expanded={expanded}
                      className="flex items-center gap-1.5 rounded-sm font-semibold hover:text-azure-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--azure-deep)]"
                    >
                      <span
                        aria-hidden="true"
                        className={`inline-block text-[11px] text-ink-soft transition-transform ${
                          expanded ? "rotate-90" : ""
                        }`}
                      >
                        ▶
                      </span>
                      Year {row.year}
                    </button>
                  </th>
                  <td className="py-2 pr-3 text-right text-ink-soft">
                    {formatCount(row.newTotal)}
                  </td>
                  <td className="py-2 pr-3 text-right text-ink-soft">
                    {row.churnTotal > 0 ? `${MINUS}${formatCount(row.churnTotal)}` : "—"}
                  </td>
                  <td className="py-2 pr-3 text-right font-semibold text-ink">
                    {formatCount(row.activeTotal)}
                  </td>
                  <td className="py-2 pr-3 text-right text-ink">{money(row.revenue)}</td>
                  <td className="py-2 pr-3 text-right text-ink">{money(row.cost)}</td>
                  <td
                    className="py-2 pr-3 text-right font-semibold"
                    style={{ color: tone(row.net) }}
                  >
                    {money(row.net)}
                  </td>
                  <td className="py-2 text-right font-semibold" style={{ color: tone(row.cumNet) }}>
                    {money(row.cumNet)}
                  </td>
                </tr>

                {expanded &&
                  subRowsFor(row).map((sub) => (
                    <tr key={sub.key} className="border-line/40 bg-chip-bg/60 border-b text-[13px]">
                      <td className="py-1.5 pl-6 pr-3 text-ink-soft">
                        <span className="flex items-center gap-1.5">
                          {sub.color && (
                            <span
                              aria-hidden="true"
                              className="inline-block h-2 w-2 shrink-0 rounded-[2px]"
                              style={{ background: sub.color }}
                            />
                          )}
                          {sub.label}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-right text-ink-soft">
                        {sub.newClients ? formatCount(sub.newClients) : "—"}
                      </td>
                      <td className="py-1.5 pr-3 text-right text-ink-soft">
                        {sub.churn ? `${MINUS}${formatCount(sub.churn)}` : "—"}
                      </td>
                      <td className="py-1.5 pr-3 text-right text-ink-soft">
                        {sub.active === undefined ? "—" : formatCount(sub.active)}
                      </td>
                      <td className="py-1.5 pr-3 text-right text-ink-soft">
                        {sub.revenue ? money(sub.revenue) : "—"}
                      </td>
                      <td className="py-1.5 pr-3 text-right text-ink-soft">
                        {sub.cost ? money(sub.cost) : "—"}
                      </td>
                      <td
                        className="py-1.5 pr-3 text-right font-semibold"
                        style={{ color: tone(sub.revenue - sub.cost) }}
                      >
                        {money(sub.revenue - sub.cost)}
                      </td>
                      <td className="py-1.5 text-right text-ink-soft">—</td>
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
